import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Spin,
  Result,
  Row,
  Col,
  Typography,
  Divider,
  Modal,
  message,
  Image,
  Select,
  Popconfirm,
} from 'antd';
import {
  EditOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { tradesApi } from '@tradermind/shared/api';
import type { TradeImageType } from '@tradermind/shared/types';
import { formatCurrency, formatDateTime } from '@tradermind/shared/utils';
import CloseTradeModal from './CloseTradeModal';

const { Title, Text, Paragraph } = Typography;

export default function TradeDetailPage() {
  const { t } = useTranslation('trade');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [uploadImageType, setUploadImageType] = useState<TradeImageType>('other');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const deleteMutation = useMutation({
    mutationFn: (tradeId: number) => tradesApi.deleteTrade(tradeId),
    onSuccess: () => {
      message.success(t('messages.delete_success'));
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      navigate('/trades');
    },
    onError: () => {
      message.error(t('messages.delete_failed'));
    },
  });

  const handleDelete = () => {
    Modal.confirm({
      title: t('detail_page.delete_confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('detail_page.delete_confirm_content'),
      okText: t('detail_page.delete'),
      okType: 'danger',
      cancelText: t('common:cancel'),
      onOk: () => deleteMutation.mutate(Number(id)),
    });
  };

  const handleUploadImages = async (files: FileList | null) => {
    if (!files || files.length === 0 || !id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append('files[]', file));
      formData.append('image_type', uploadImageType);
      await tradesApi.uploadTradeImages(Number(id), formData);
      message.success(t('detail_page.upload_success'));
      queryClient.invalidateQueries({ queryKey: ['trade', id] });
    } catch {
      message.error(t('detail_page.upload_failed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteImageMutation = useMutation({
    mutationFn: ({ tradeId, imageId }: { tradeId: number; imageId: number }) =>
      tradesApi.deleteTradeImage(tradeId, imageId),
    onSuccess: () => {
      message.success(t('detail_page.image_deleted'));
      queryClient.invalidateQueries({ queryKey: ['trade', id] });
    },
    onError: () => {
      message.error(t('detail_page.image_delete_failed'));
    },
  });

  const imageTypeLabels: Record<TradeImageType, string> = {
    entry: t('image_type.entry'),
    exit: t('image_type.exit'),
    analysis: t('image_type.analysis'),
    other: t('image_type.other'),
  };

  const imageTypeColors: Record<TradeImageType, string> = {
    entry: 'green',
    exit: 'red',
    analysis: 'blue',
    other: 'default',
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['trade', id],
    queryFn: () => tradesApi.getTradeById(Number(id)),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <Result
        status="404"
        title={t('detail_page.not_found')}
        subTitle={t('detail_page.not_found_desc')}
        extra={
          <Button type="primary" onClick={() => navigate('/trades')}>
            {t('detail_page.back_list')}
          </Button>
        }
      />
    );
  }

  const trade = data.data;

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/trades')}>
            {t('detail_page.back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {trade.symbol}
            <Tag
              color={trade.direction === 'long' ? 'green' : 'red'}
              style={{ marginLeft: 8 }}
            >
              {trade.direction === 'long' ? t('side.long_full') : t('side.short_full')}
            </Tag>
            <Tag color={trade.status === 'open' ? 'blue' : 'default'} style={{ marginLeft: 4 }}>
              {trade.status === 'open' ? t('status.open') : t('status.closed')}
            </Tag>
          </Title>
        </Space>
        <Space>
          {trade.status === 'open' && (
            <Button type="primary" onClick={() => setCloseModalVisible(true)}>
              {t('detail_page.close_position')}
            </Button>
          )}
          <Button icon={<EditOutlined />} onClick={() => navigate(`/trades/${id}/edit`)}>
            {t('detail_page.edit')}
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('detail_page.delete')}
          </Button>
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card title={t('detail_page.trade_info')}>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered>
              <Descriptions.Item label={t('detail_page.symbol')}>{trade.symbol}</Descriptions.Item>
              <Descriptions.Item label={t('detail_page.direction')}>
                <Tag color={trade.direction === 'long' ? 'green' : 'red'}>
                  {trade.direction === 'long' ? t('side.long_full') : t('side.short_full')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('detail_page.entry_price')}>{trade.entry_price.toFixed(4)}</Descriptions.Item>
              <Descriptions.Item label={t('detail_page.exit_price')}>
                {trade.exit_price ? trade.exit_price.toFixed(4) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail_page.quantity')}>{trade.quantity}</Descriptions.Item>
              <Descriptions.Item label={t('detail_page.leverage')}>{trade.leverage || 1}x</Descriptions.Item>
              <Descriptions.Item label={t('detail_page.open_time')}>{formatDateTime(trade.open_time)}</Descriptions.Item>
              <Descriptions.Item label={t('detail_page.close_time')}>
                {trade.close_time ? formatDateTime(trade.close_time) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail_page.pnl')}>
                {trade.profit_loss !== undefined ? (
                  <Text
                    style={{
                      color: trade.profit_loss >= 0 ? '#10B981' : '#EF4444',
                      fontWeight: 600,
                      fontSize: 18,
                    }}
                  >
                    {trade.profit_loss >= 0 ? '+' : ''}
                    {formatCurrency(trade.profit_loss)}
                  </Text>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail_page.commission')}>
                {trade.commission ? formatCurrency(trade.commission) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('risk_amount')}>
                {trade.risk_amount ? formatCurrency(trade.risk_amount) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('r_multiple')}>
                {trade.r_multiple !== undefined && trade.r_multiple !== null ? (
                  <Tag
                    color={
                      trade.r_multiple > 0
                        ? 'green'
                        : trade.r_multiple < 0
                        ? 'red'
                        : 'default'
                    }
                    style={{ fontSize: 14, fontWeight: 600 }}
                  >
                    {trade.r_multiple > 0 ? '+' : ''}
                    {trade.r_multiple}R
                  </Tag>
                ) : (
                  <Text type="secondary">{trade.risk_amount ? t('r_multiple_auto') : t('r_multiple_hint')}</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail_page.strategy')}>{trade.strategy || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('detail_page.quality_score')}>
                {(trade.checklist_score ?? trade.quality_score) !== undefined ? (
                  <Tag
                    color={
                      ((trade.checklist_score ?? trade.quality_score) || 0) >= 80
                        ? 'green'
                        : ((trade.checklist_score ?? trade.quality_score) || 0) >= 60
                        ? 'orange'
                        : 'red'
                    }
                  >
                    {trade.checklist_score ?? trade.quality_score}分
                  </Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {trade.notes && (
            <Card title={t('detail_page.trade_notes')} style={{ marginTop: 24 }}>
              <Paragraph>{trade.notes}</Paragraph>
            </Card>
          )}

          {/* 交易截图 */}
          <Card
            title={
              <Space>
                <CameraOutlined />
                {t('detail_page.trade_images')}
              </Space>
            }
            style={{ marginTop: 24 }}
            extra={
              <Space>
                <Select
                  value={uploadImageType}
                  onChange={setUploadImageType}
                  size="small"
                  style={{ width: 90 }}
                  options={[
                    { value: 'entry', label: imageTypeLabels.entry },
                    { value: 'exit', label: imageTypeLabels.exit },
                    { value: 'analysis', label: imageTypeLabels.analysis },
                    { value: 'other', label: imageTypeLabels.other },
                  ]}
                />
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  loading={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('detail_page.upload_image')}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={(e) => handleUploadImages(e.target.files)}
                />
              </Space>
            }
          >
            {trade.images && trade.images.length > 0 ? (
              <Image.PreviewGroup>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {trade.images.map((img) => (
                    <div key={img.id} style={{ position: 'relative', textAlign: 'center' }}>
                      <Image
                        src={img.file_url}
                        alt={img.caption || img.original_filename}
                        width={160}
                        height={120}
                        style={{ objectFit: 'cover', borderRadius: 8 }}
                      />
                      <div style={{ marginTop: 4 }}>
                        <Tag color={imageTypeColors[img.image_type as TradeImageType]}>
                          {imageTypeLabels[img.image_type as TradeImageType]}
                        </Tag>
                        <Popconfirm
                          title={t('detail_page.delete_image_confirm')}
                          onConfirm={() => deleteImageMutation.mutate({ tradeId: trade.id, imageId: img.id })}
                          okText={t('common:confirm')}
                          cancelText={t('common:cancel')}
                        >
                          <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </div>
                      {img.caption && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                          {img.caption}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              </Image.PreviewGroup>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#999' }}>
                {t('detail_page.no_images')}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t('detail_page.emotion_record')}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">{t('detail_page.pre_emotion')}</Text>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {trade.pre_trade_emotion ? `${trade.pre_trade_emotion}/10` : '-'}
              </div>
            </div>
            <Divider />
            <div>
              <Text type="secondary">{t('detail_page.post_emotion')}</Text>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {trade.post_trade_emotion ? `${trade.post_trade_emotion}/10` : '-'}
              </div>
            </div>
          </Card>

          {trade.tags && trade.tags.length > 0 && (
            <Card title={t('detail_page.tags')} style={{ marginTop: 24 }}>
              <Space wrap>
                {trade.tags.map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </Space>
            </Card>
          )}
        </Col>
      </Row>

      <CloseTradeModal
        trade={trade}
        visible={closeModalVisible}
        onClose={() => setCloseModalVisible(false)}
        onSuccess={() => {}}
      />
    </div>
  );
}
